import {AutomationNode, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {TFunction} from 'i18next';
import {buildFieldOptions, fieldReference, IfConditionGroup, IfConditionRule, IfFieldOption, NO_VALUE_OPERATORS, readConditionGroups, writeConditionGroups} from './ifConditions';
import {getNodeOutputs} from './nodeOutputs';
import {stepName} from './nodeCategories';

export const contactHistoryField = (label: string, channel: string, reservation = false) =>
    fieldReference(label, `contactHistory.${channel}${reservation ? '.RESERVATION' : ''}`);

export const isContactHistoryField = (field: string) => /\.contactHistory\.(EMAIL|PUSH|ANY)(\.RESERVATION)?}}$/.test(field);

/** Labels of the fresh-state paths (`currentGuest.…`), shared by the rule picker and the card summary. */
export const CURRENT_SCOPE_LABELS: Record<string, string> = {
    currentGuest: 'Current guest', currentReservation: 'Current reservation', currentInvitation: 'Current invitation'
};

export const CURRENT_INVITATION_FIELD_LABELS: Record<string, string> = {
    answeredAt: 'Answered at', voidedAt: 'Voided at', lastSentAt: 'Last invitation sent', sendCount: 'Invitation sends', reminderCount: 'Reminder sends'
};

export const continueIfFields = (nodes: AutomationNode[], t: TFunction = ((key: string) => key) as TFunction, channels: string[] = []): IfFieldOption[] => {
    const fields: IfFieldOption[] = [
        ...buildFieldOptions(nodes, t).map(field => ({...field, value: field.value.replace(/\.(emails|phones|addresses)\./, '.$1[0].')})),
        ...nodes.flatMap(node => {
            const label = stepName(node);
            const outputNames = getNodeOutputs(node.type).map(output => output.name);
            const fields: IfFieldOption[] = [];
            const carriesGuest = node.type === NodeType.AUDIENCE || outputNames.some(name => ['profileId', 'primaryProfileId'].includes(name));
            if (carriesGuest) {
                getNodeOutputs(NodeType.AUDIENCE).filter(output => !['audienceName', 'audienceSize', 'profileIndex'].includes(output.name)).forEach(output => fields.push({
                    ...output, value: fieldReference(label, `currentGuest.${output.name.replace(/^(emails|phones|addresses)\./, '$1[0].')}`),
                    label: t(output.label || output.name), group: t('Current guest · {{step}}', {step: label})
                }));
            }
            const carriesReservation = node.type === NodeType.SURVEY_INVITE
                ? !!node.config?.reservationId || (!node.config?.profileId && nodes.some(source =>
                    [NodeType.RESERVATION_CREATED, NodeType.RESERVATION_STATUS_CHANGED].includes(source.type)))
                : outputNames.includes('reservationId') || [NodeType.RESERVATION_CREATED, NodeType.RESERVATION_STATUS_CHANGED].includes(node.type);
            if (carriesGuest) {
                [...channels, ...(channels.length > 1 ? ['ANY'] : [])].forEach(channel => {
                    const name = channel === 'EMAIL' ? 'Emails from this automation' : channel === 'PUSH' ? 'Push notifications from this automation' : 'Messages from this automation';
                    fields.push({value: contactHistoryField(label, channel), label: t(name), group: label, type: 'contactHistory'});
                    if (carriesReservation) fields.push({
                        value: contactHistoryField(label, channel, true),
                        label: t(channel === 'EMAIL' ? 'Emails for this reservation' : channel === 'PUSH' ? 'Push notifications for this reservation' : 'Messages for this reservation'),
                        group: label, type: 'reservationContactHistory'
                    });
                });
            }
            if (carriesReservation) {
                getNodeOutputs(NodeType.RESERVATION_CREATED).filter(output => ['id', 'status', 'startDate', 'endDate', 'startDay', 'endDay', 'primaryProfileId'].includes(output.name)).forEach(output => fields.push({
                    ...output, value: fieldReference(label, `currentReservation.${output.name}`),
                    label: t(output.label || output.name), group: t('Current reservation · {{step}}', {step: label})
                }));
            }
            if (outputNames.includes('invitationId')) {
                Object.keys(CURRENT_INVITATION_FIELD_LABELS).forEach(name => fields.push({
                    value: fieldReference(label, `currentInvitation.${name}`), type: name.endsWith('Count') ? 'number' : 'date',
                    label: t(CURRENT_INVITATION_FIELD_LABELS[name]), group: t('Current invitation · {{step}}', {step: label})
                }));
            }
            return fields;
        })
    ];
    // Keep each source's options together for the shared autocomplete's group headers.
    const grouped = new Map<string, IfFieldOption[]>();
    fields.forEach(field => grouped.set(field.group, [...(grouped.get(field.group) || []), field]));
    return Array.from(grouped.values()).flat();
};

/** Legacy `contact*` keys read as a contact-history rule; only writeContinueIfGroups replaces them. */
export const readContinueIfGroups = (config: Record<string, any> = {}, channels: string[] = []): IfConditionGroup[] => {
    const groups = readConditionGroups(config);
    const mode = config.contactLimitMode;
    if (!mode || mode === 'NONE') return groups;
    const reservation = config.contactIdentity === 'RESERVATION';
    const source = reservation && config.contactReservationId ? config.contactReservationId : config.contactProfileId;
    const label = typeof source === 'string' ? source.match(/^\{\{"([^"]+)"\./)?.[1] : undefined;
    const channel = config.contactChannel && config.contactChannel !== 'ANY' ? config.contactChannel : channels.length === 1 ? channels[0] : 'ANY';
    const rule: IfConditionRule = {
        field: contactHistoryField(label || 'Guest', channel, reservation),
        operator: mode === 'ONCE' ? 'contact_not_received' : mode === 'LIMIT' ? 'contact_fewer_than_in_days' : 'contact_not_received_in_days',
        value: mode === 'ONCE' ? undefined : mode === 'LIMIT' ? {count: config.contactMax, days: config.contactDays} : config.contactDays
    };
    return [{operator: 'AND', rules: [rule], ...(groups.length ? {groups: [{operator: 'OR' as const, groups}]} : {})}];
};

export const writeContinueIfGroups = (config: Record<string, any>, groups: IfConditionGroup[]): Record<string, any> => ({
    ...config, ...writeConditionGroups(groups),
    contactLimitMode: undefined, contactProfileId: undefined, contactReservationId: undefined,
    contactIdentity: undefined, contactScope: undefined, contactScopeId: undefined,
    contactChannel: undefined, contactDays: undefined, contactMax: undefined,
});

export const continueIfError = (config: Record<string, any>, fields?: IfFieldOption[]): string | undefined => {
    const groups = readContinueIfGroups(config);
    const available = fields && new Set(fields.map(field => field.value));
    const complete = (group: IfConditionGroup): boolean => {
        const rules = group.rules || [], nested = group.groups || [];
        return (rules.length + nested.length > 0) && rules.every(rule => {
            if (!rule.field || !rule.operator) return false;
            if (available && !available.has(rule.field)) return false;
            const whole = (value: any, max: number) => Number.isInteger(value) && value >= 1 && value <= max;
            if (isContactHistoryField(rule.field)) {
                switch (rule.operator) {
                    case 'contact_not_received': return true;
                    case 'contact_not_received_in_days': return whole(rule.value, 3650);
                    case 'contact_fewer_than': return whole(rule.value, 100);
                    case 'contact_fewer_than_in_days': return whole(rule.value?.count, 100) && whole(rule.value?.days, 3650);
                    default: return false;
                }
            }
            if (['older_than_days', 'within_last_days'].includes(rule.operator)) return whole(rule.value, 3650);
            return NO_VALUE_OPERATORS.includes(rule.operator) || (rule.value != null && rule.value !== '');
        }) && nested.every(complete);
    };
    if (groups.some(group => !complete(group))) return 'Finish every condition or remove the unfinished rules.';
    if (!groups.length) return 'Add a condition to decide who continues.';
};
